import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, StatTile, StatusBadge, ToggleSwitch, useLocale, type DataTableColumn } from '@ministry/ui/internal';

import { listMyTasks, type MyTaskRow } from '../lib/workflow.js';
import { fileStageEntry, OVERDUE_FLAG } from '../lib/file-lifecycle.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { formatDueDisplay } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';

/**
 * SCREEN_SPECS.md I10. `GET /me/tasks` has no `sort` parameter in the running server's
 * schema (`workflow-routes.ts`'s `tasksQuerySchema` accepts only `isOverdue`) despite
 * API_CONTRACT.md's prose claiming `dueAt`/`registeredAt` sort — so this screen offers no
 * sort control, only the one filter the server actually accepts.
 */
export function MyTasksPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [overdueOnly, setOverdueOnly] = React.useState(false);

  const query = useQuery({
    queryKey: ['my-tasks', overdueOnly],
    queryFn: ({ signal }) => listMyTasks({ isOverdue: overdueOnly || undefined }, signal),
  });

  const rows = query.data ?? [];
  const overdueCount = rows.filter((row) => row.isOverdue).length;

  const allColumns: DataTableColumn<MyTaskRow>[] = [
    {
      key: 'trackingCode',
      header: t('myTasks.column.trackingCode'),
      visibleFrom: 'always',
      cardSlot: 'title',
      render: (row) => <span className="whitespace-nowrap font-mono">{row.trackingCode}</span>,
    },
    {
      key: 'statusCode',
      header: t('myTasks.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={fileStageEntry(row.statusCode)} />,
    },
    // Overdue is an orthogonal flag "appended to any [stage], not a stage of its own"
    // (DESIGN_SYSTEM.md §9.1). Included below only when at least one row in view is
    // actually overdue — a dedicated column that was blank on every row was the named
    // defect ("one column renders entirely empty").
    {
      key: 'overdue',
      header: t('fileRegister.column.overdue'),
      visibleFrom: 'always',
      cardSlot: 'overdueBadge',
      render: (row) => (row.isOverdue ? <StatusBadge entry={OVERDUE_FLAG} variant="solid" /> : null),
    },
    {
      key: 'subject',
      header: t('myTasks.column.subject'),
      visibleFrom: 'always',
      cardSlot: 'line2',
      // The subject — "what the file actually is" — is the column that should absorb the
      // table's leftover width, not the tracking code (ux-review defect: "the tracking
      // code takes ~320px while the subject … is pushed to the far right").
      grow: true,
      render: (row) => row.subject,
    },
    {
      key: 'dueDate',
      header: t('myTasks.column.dueDate'),
      visibleFrom: 'md',
      cardSlot: 'line4',
      minWidth: '9rem',
      render: (row) => (row.dueAt ? formatDueDisplay(row.dueAt, locale) : t('myTasks.noDueDate')),
    },
  ];

  const columns = overdueCount > 0 ? allColumns : allColumns.filter((c) => c.key !== 'overdue');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('myTasks.title')} />

      <div className="flex flex-wrap gap-3">
        <StatTile
          className="min-w-[220px] flex-1"
          label={t('myTasks.statTotal')}
          value={rows.length}
          loading={query.isLoading}
          error={query.isError}
          onRetry={() => query.refetch()}
        />
        <StatTile
          className="min-w-[220px] flex-1"
          label={t('myTasks.statOverdue')}
          value={overdueCount}
          loading={query.isLoading}
          error={query.isError}
          onRetry={() => query.refetch()}
          accent={overdueCount > 0 ? 'serious' : 'default'}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 border-b border-border-decorative pb-4">
        <ToggleSwitch id="my-tasks-overdue-only" label={t('myTasks.filterOverdueOnly')} checked={overdueOnly} onCheckedChange={setOverdueOnly} />
      </div>

      <DataTable
        caption={t('myTasks.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        onRowClick={(row) => navigate(ROUTES.fileDetail(row.id))}
        emptyTitle={t('myTasks.empty.title')}
        emptyBody={t('myTasks.empty.body')}
      />
    </div>
  );
}
